import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { faShareNodes } from "@fortawesome/pro-regular-svg-icons";

import { Icon, Modal } from "@/components";
import { ButtonColor } from "@/components/Button/types";
import { Radio } from "@/components/Radio/Radio";
import { campaignStateLabel } from "@/components/OutreachActivation/outreachActivation";

import { classNames } from "@/utils/classNames";

import { listApi } from "@/pages/Private/redux/list/list.api";

import { LeadAlreadyConnectedNotice } from "../../../../List/components/ReachOut/alreadyConnected/LeadAlreadyConnectedNotice";
import { CopywritingCampaign } from "../../../../List/pages/ListEditCopywriting/ListEditCopywriting";
import { ListPerson } from "../../../../List/schema/list";

// Short "Email · LinkedIn · 4 steps" descriptor for a campaign row in the Assign-to-campaign picker.
export const getCampaignMeta = (campaign: CopywritingCampaign): string => {
	const nodes = (campaign?.initialNodes ?? []) as { type?: string }[];
	const hasEmail = nodes.some((n) => n.type === "emailNode");
	const hasLinkedin = nodes.some((n) => (n.type ?? "").toLowerCase().includes("linkedin"));
	const steps = nodes.filter(
		(n) => !["starterNode", "conditionalNode", "waitNode"].includes(n.type ?? "")
	).length;

	const channels = [hasEmail && "Email", hasLinkedin && "LinkedIn"].filter(Boolean) as string[];

	return [...channels, `${steps} step${steps === 1 ? "" : "s"}`].join(" · ");
};

interface AssignCampaignPickerModalProps {
	listId: number;
	campaigns: CopywritingCampaign[];
	selectedCampaignId?: string;
	isLoading: boolean;
	onSelect: (campaign: CopywritingCampaign) => void;
	onClose: () => void;
	onAssignAndSend: () => void;
	onPreAssign: () => void;
	/** The lead being assigned: the picked campaign's sender may already be connected (PREC-4426). */
	lead?: ListPerson;
}

/**
 * Review-to-send's "Assign to campaign" picker. Each row is labelled with **that campaign's** run
 * state, read from the campaign summary (PREC-3296): it used to read the list-wide rollup, so on a
 * running list a campaign that had never been started said "Active" — and the send path now asks to
 * start exactly such a campaign, which would have contradicted the row the user had just picked.
 * The old "Activate outreach for this list" toggle is gone: "Assign & send out" asks, per campaign.
 */
export const AssignCampaignPickerModal: FunctionComponent<AssignCampaignPickerModalProps> = ({
	listId,
	campaigns,
	selectedCampaignId,
	isLoading,
	onSelect,
	onClose,
	onAssignAndSend,
	onPreAssign,
	lead,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`messages.${key}`);

	// Through the endpoint: list.api.ts is at its size ceiling, and this is the only non-lazy reader.
	const { data: summary } = listApi.endpoints.getListCampaignSummary.useQuery(
		{ listId },
		{ skip: !listId }
	);

	const selectedCampaign = campaigns.find((campaign) => campaign.id === selectedCampaignId);

	const renderState = (campaignId: string) => {
		const status = summary?.data.find((row) => row.campaignId === campaignId)?.status;

		if (!status) {
			return null;
		}

		const { key, sending } = campaignStateLabel(status);

		return (
			<span
				className={classNames(
					"text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap shrink-0",
					sending
						? "bg-status-success-light text-status-success"
						: "bg-status-warning-light text-status-warning"
				)}
			>
				{t(key)}
			</span>
		);
	};

	return (
		<Modal
			isOpened
			disableSave={!selectedCampaignId}
			disableSecondSave={!selectedCampaignId}
			handleClose={onClose}
			handleSave={onAssignAndSend}
			handleSecondSave={onPreAssign}
			icon={<Icon className="w-5 h-5 text-brand-light" icon={faShareNodes} />}
			isLoading={isLoading}
			secondSubmitButtonColor={ButtonColor.ACTION_SECONDARY}
			secondSubmitButtonText={ts("onlyPreAssign")}
			size="sm"
			submitButtonColor={ButtonColor.SUCCESS}
			submitButtonText={ts("assignAndSendOut")}
			subtitle={ts("assignToCampaignSubtitle")}
			title={ts("assignToCampaign")}
		>
			<div className="flex flex-col gap-2">
				{campaigns.map((campaign) => {
					const isSelected = selectedCampaignId === campaign.id;

					return (
						<div
							key={campaign.id}
							className={classNames(
								"flex flex-row items-center gap-3 px-3 py-3 rounded-lg border cursor-pointer transition-colors",
								isSelected
									? "border-brand-light bg-brand-white"
									: "border-border-light hover:border-brand-light/50 hover:bg-brand-white/50"
							)}
							onClick={() => onSelect(campaign)}
						>
							<Radio isChecked={isSelected} onChange={() => onSelect(campaign)} />
							<div className="flex flex-col min-w-0 flex-1">
								<span className="text-sm font-semibold text-text truncate">{campaign.name}</span>
								<span className="text-xs text-text-meta truncate">{getCampaignMeta(campaign)}</span>
							</div>
							{renderState(campaign.id)}
						</div>
					);
				})}
				{lead && selectedCampaign && (
					<LeadAlreadyConnectedNotice campaign={selectedCampaign} lead={lead} />
				)}
				{campaigns.length === 0 && (
					<div className="text-sm text-text-meta py-2">
						{t("outreachActivation.noValidCampaigns")}
					</div>
				)}
			</div>
		</Modal>
	);
};
